<template>
  <div :class="{ 'is-fetching': isFetching }" class="works">
    <VVideo
      :content="video"
      :options="{ controls: ['play', 'mute', 'fullscreen'] }"
      class="wrapper m-d"
    />
    <Filter
      v-if="content.all?.length"
      ref="$filter"
      :content="content.all"
      :active="currentFilter"
      @update="onFilterUpdate"
      class="wrapper m-d works__filter"
    />

    <div
      v-if="cards.length"
      ref="grid"
      :key="key"
      class="works__items wrapper m-d"
    >
      <CaseCard
        v-for="(item, idx) in cards"
        :key="item.title"
        :content="item"
        class="works__items__item is-visible"
        data-stagger
      />
    </div>

    <div class="wrapper m-d">
      <GBtn
        v-if="loadMoreUrl"
        ref="more"
        tag="a"
        :href="loadMoreUrl"
        size="small"
        class="works__more"
        :class="{ disabled: isFetching }"
        @click.prevent="onLoadMore"
      >
        {{ content.loadMoreLink?.label }}
        <img
          v-if="isFetching"
          src="@/assets/images/loading.svg"
          alt="Loading"
        />
      </GBtn>
    </div>

    <div class="wrapper">
      <hr />
      <UiCta
        v-if="content.servicesCta"
        :content="content.servicesCta"
        class="m-d"
      />
      <hr />
    </div>

    <div class="m-d wrapper works__listing">
      <span class="section-label">{{ content.oldHeadline }}</span>
    </div>

    <Listing v-if="content.old?.length" :content="content.old" class="m-d" />

    <div class="wrapper">
      <hr />
      <UiCta v-if="content.cta" :content="content.cta" class="m-d" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import {
  onMounted,
  ref,
  shallowRef,
  nextTick,
  onBeforeMount,
  watch,
  onBeforeUnmount,
} from 'vue'
import { LocationQuery, useRoute, useRouter } from 'vue-router'

import Filter from '@/components/portfolio/FIlter.vue'
import VVideo from '@/components/ui/Video.vue'
import UiCta from '@/components/ui/Cta.vue'
import Listing from '@/components/Listing.vue'
import CaseCard from '@/components/ui/CaseCard.vue'

import { push } from '@/utils/tracking'
import { useAPI } from '@/core/resource'
import { useResourceStore } from '@/stores/resource'

const props = defineProps<{ content: any }>()
const route = useRoute()
const { fetchAPI } = useAPI()
const resourceStore = useResourceStore()

const cards = shallowRef([...(props.content?.items || [])])
const page = ref(route.query.page ? Number(route.query.page) : 1)
const currentFilter = ref((route.query.category as string) ?? 'highlights')
const video = {
  id: '974135712',
  icon: 'play',
}

const key = ref(0)
const grid = ref<HTMLElement>()
const getCards = () =>
  [...(grid.value?.querySelectorAll('[data-stagger]') || [])].filter(
    card => (card as HTMLElement).dataset.visible === undefined
  )

const revealCards = () => {
  nextTick(() => {
    const cards = getCards()
    cards.forEach(card => ((card as HTMLElement).dataset.visible = 'true'))
    gsap.killTweensOf(cards)
    gsap.set(cards, { y: 30, opacity: 0 })

    gsap.to(cards, {
      y: 0,
      opacity: 1,
      duration: 0.15,
      stagger: 0.03,
      ease: 'none',
    })
  })
}

const hideCards = async () => {
  const cards = getCards()
  gsap.killTweensOf(cards)
  gsap.to(window, {
    scrollTo: {
      y: $filter.value?.$el || 0,
      autoKill: false,
    },
  })
  await gsap.to(cards, {
    y: -30,
    opacity: 0,
    duration: 0.15,
    stagger: 0.03,
    ease: 'none',
  })
}

const updateCards = async () => {
  try {
    // Update head content when fetching new content
    // since each service now come with its own meta tags.
    // @see https://git.epic.net/epic/website-v3/-/work_items/92#note_471690
    const newCards = await fetch(true)

    hideCards()

    cards.value = newCards
    key.value += 1

    revealCards()
  } catch (error) {
    console.error(error)
  }
}

const loadMoreUrl = ref(props.content?.loadMoreLink?.url)
const $filter = ref<InstanceType<typeof Filter>>()
const onFilterUpdate = async (filter: string) => {
  page.value = 1
  currentFilter.value = filter
  push({ event: 'works_categories', worksCategories: filter })
  await updateCards()
}

const updateUrl = () => {
  const url = new URL(window.location.href)

  if (page.value === 1) {
    url.searchParams.delete('page')
  } else {
    url.searchParams.set('page', String(page.value))
  }

  if (currentFilter.value === 'highlights') {
    url.searchParams.delete('category')
  } else {
    url.searchParams.set('category', currentFilter.value)
  }

  window.history.pushState(window.history.state, '', url)
}
watch([page, currentFilter], updateUrl)

const isFetching = ref(false)

const fetch = async (updateHead = false) => {
  isFetching.value = true
  const query: LocationQuery = {
    ...route.query,
    category: currentFilter.value,
    page: String(page.value),
  }

  currentFilter.value === 'highlights' && delete query.category

  const data = await fetchAPI({ ...route, query })

  isFetching.value = false
  loadMoreUrl.value = data?.content?.loadMoreLink?.url

  if (updateHead && data?.head) {
    resourceStore.$patch({ head: data.head })
  }

  return data?.content?.items || []
}

const more = ref()

const onLoadMore = async () => {
  page.value += 1

  // Don't update head content when loading more
  const newCards = await fetch(false)
  cards.value = [...cards.value, ...newCards]
  revealCards()
}

const handleURL = async e => {
  const url = new URL(e.target.location.href)
  const c = url.searchParams.get('category')
  const p = url.searchParams.get('page')
  c?.length && (currentFilter.value = c)
  p?.length && (page.value = Number(p))
  await updateCards()
}

onMounted(() => {
  window.addEventListener('popstate', handleURL)
  nextTick(() => {
    const cards = getCards()
    cards.forEach(card => ((card as HTMLElement).dataset.visible = 'true'))
  })
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handleURL)
})
</script>

<style lang="scss" scoped>
.works {
  &.is-fetching {
    cursor: wait;
  }
}

.works__filter {
  .works.is-fetching & {
    pointer-events: none;
  }
}

.works__listing {
  text-align: center;
}

.works__items {
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;

  .is-moving & {
    min-height: 100vh;
  }

  @include mq(s) {
    grid-template-columns: 1fr 1fr;
  }
}

.works__items__item {
  // prettier-ignore
  @include fluid(height,(xxs: 200px, xxl: 380px));

  &:not(.is-visible) {
    display: none;
  }
}

.works__more {
  width: fit-content;
  margin: 0 auto;

  :deep(.btn__label) {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  :deep(.btn__icon) {
    /* stylelint-disable-next-line declaration-no-important */
    transform: rotate(90deg) !important;
  }
}
</style>
